import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Text, Button, Notification } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { Page } from 'styles';
import { BackLink } from 'components/back-link';
import { useUser } from 'hooks/use-user.hook';
import { trackEvent, trackError } from 'domain/tracking';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { ContractPreviewDialog } from './components/contract-preview-dialog/contract-preview-dialog.component';
import { QualificationFields } from './components/qualification-fields/qualification-fields.component';
import { stateConfigs } from './configs';
import { FormField, QualificationType } from './configs/types';
import {
  fetchLmiContractData,
  createPersonalizedContractPreview,
  createContractAgreements,
  prepareTabsDataForPreview,
} from './services/lmi-contract.api';
import {
  TabsData,
  TabsDataType,
} from './components/tabs-data/tabs-data.component';
import {
  FormContainer,
  SectionTitle,
  SectionDescription,
  EligibilitySection,
  ButtonContainer,
} from './lmi-contract-form.style';

const getCopy = copyFor('settings.checkLmiEligibility.selfAttestationModal');
const { settings } = routes();

interface LocationState {
  utilityAccountId: number;
  utilityId: number;
  state: string;
  stateId: number;
  zipCode: string;
  utilityName: string;
  validProofDocs?: ({
    proofDocUrls: (string | null)[];
    proofDocName: string | null;
    proofDocStatus: string | null;
    proofDocUploadedAt: string | null;
    uaLmiSourceTypeId: number | null;
    utilityAccountId: number | null;
    utilityName: string | null;
    utilityAccountNumber: string | null;
    utilityServiceAddress: string | null;
  } | null)[];
}

export const LmiContractForm = (): JSX.Element | null => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { data: user } = useUser();
  const { addNotification } = useContext(Notification.Context);

  // Get data from location state
  const {
    utilityAccountId,
    utilityId,
    state: selectedState,
    stateId,
    zipCode,
    utilityName,
    validProofDocs,
  } = location.state || {};

  // Redirect back if missing required data
  useEffect(() => {
    if (!utilityAccountId || !selectedState) {
      history.push(`${settings}/check-lmi-eligibility`);
    }
  }, [utilityAccountId, selectedState, history]);

  // Get state config
  const config = useMemo(() => {
    return selectedState ? stateConfigs[selectedState] : null;
  }, [selectedState]);

  // Data fetching states
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [counties, setCounties] = useState<string[]>([]);
  const [incomeLimits, setIncomeLimits] = useState<Record<string, string[]>>(
    {}
  );
  const [programOptions, setProgramOptions] = useState<Record<
    string,
    string | string[]
  > | null>(null);
  const [contract, setContract] = useState<{
    id: number;
    displayName: string;
    fileUrl: string;
  } | null>(null);

  const [tabsData, setTabsData] = useState<TabsDataType | null>(null);
  const [qualificationType, setQualificationType] = useState<string | string[]>(
    ''
  );

  const [showPreview, setShowPreview] = useState(false);
  const [personalizedContractUrl, setPersonalizedContractUrl] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSubmittingContract, setIsSubmittingContract] = useState(false);

  useEffect(() => {
    if (!config || !user || !utilityId || !stateId || !zipCode) return;

    const fetchData = async () => {
      setIsLoadingData(true);
      setDataError(null);

      trackEvent('lmi_contract_form_opened', {
        state: selectedState,
        utilityAccountId,
        userId: user.id,
      });

      try {
        const {
          counties,
          incomeLimits,
          programOptions: programs,
          contract: contractVersion,
        } = await fetchLmiContractData({
          userId: user.id,
          utilityId,
          stateId,
          zipCode,
          category: config.lmiProofCategory,
          tag: config.contractTag ? [config.contractTag] : [],
        });

        setCounties(counties || []);
        setIncomeLimits(incomeLimits || {});
        setProgramOptions(programs);
        setContract(contractVersion);

        trackEvent('lmi_contract_form_data_loaded', {
          state: selectedState,
          countiesCount: counties?.length || 0,
          programOptionsCount: programs ? Object.keys(programs).length : 0,
        });
      } catch (error: unknown) {
        trackError({
          error: new Error('Failed to fetch LMI contract data'),
          extras: {
            originalError: error,
            utilityAccountId,
            state: selectedState,
          },
          tags: {
            module: 'lmi-contract-form',
            action: 'fetch-data',
          },
        });

        trackEvent('lmi_contract_form_data_fetch_failed', {
          state: selectedState,
          errorMessage:
            error instanceof Error ? error.message : 'Unknown error',
        });

        setDataError(getCopy('dataFetchError'));
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [
    config,
    user,
    utilityId,
    stateId,
    zipCode,
    utilityAccountId,
    selectedState,
  ]);

  const handleTabsDataChange = useCallback((data: TabsDataType) => {
    setTabsData(data);
  }, []);

  const getQualificationField = useCallback((): FormField | null => {
    return (
      config?.fields.find(
        f => f.name === 'qualificationType' || f.name === 'incomeAttestation'
      ) || null
    );
  }, [config]);

  const getInterpolations = useCallback((): Record<string, string> => {
    const lmiProgram = tabsData?.lmiProgram;
    const programName = Array.isArray(lmiProgram)
      ? lmiProgram.join(', ')
      : lmiProgram || '';
    return {
      incomeLimit: tabsData?.houseIncome ? `${tabsData.houseIncome}` : '',
      programName,
      customerName:
        user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
    };
  }, [tabsData, user]);

  const isFormReady = useCallback(() => {
    if (!tabsData || !config) return false;

    const qualField = getQualificationField();
    const hasQualificationType =
      qualField &&
      ((qualField.type === 'radio' &&
        typeof qualificationType === 'string' &&
        qualificationType) ||
        (qualField.type === 'checkbox' &&
          Array.isArray(qualificationType) &&
          qualificationType.length >= (qualField.minSelections ?? 1)));

    switch (selectedState) {
      case 'MD':
        return !!tabsData.houseOccupancy && !!hasQualificationType;
      case 'NY':
      case 'VA':
        return (
          !!tabsData.county &&
          !!tabsData.houseOccupancy &&
          !!hasQualificationType
        );
      case 'MN':
        return (
          !!tabsData.county &&
          !!tabsData.houseOccupancy &&
          !!tabsData.lmiProgram &&
          !!hasQualificationType
        );
      case 'NJ':
      case 'MA':
        if (qualificationType === QualificationType.INCOME) {
          return (
            !!tabsData.county &&
            !!tabsData.houseOccupancy &&
            !!tabsData.houseIncome &&
            !!hasQualificationType
          );
        }
        return (
          !!tabsData.county &&
          !!tabsData.houseOccupancy &&
          !!tabsData.lmiProgram &&
          !!hasQualificationType
        );
      default:
        return false;
    }
  }, [
    tabsData,
    config,
    selectedState,
    qualificationType,
    getQualificationField,
  ]);

  const handlePreviewContract = async () => {
    if (!user || !utilityAccountId || !tabsData) return;

    if (!contract) {
      addNotification({
        message: getCopy('contractNotAvailable'),
        variant: 'error',
        displayTime: NOTIFICATION_DISPLAY_TIME,
      });
      return;
    }
    setIsLoadingPreview(true);

    trackEvent('lmi_contract_form_preview_requested', {
      state: selectedState,
      county: tabsData.county,
      houseOccupancy: tabsData.houseOccupancy,
      lmiProgram: tabsData.lmiProgram,
    });

    try {
      const tabsDataInput = prepareTabsDataForPreview(
        tabsData,
        qualificationType
      );
      const response = await createPersonalizedContractPreview({
        contractVersionId: contract.id,
        utilityAccountId,
        tabsData: tabsDataInput,
      });

      const pdfUrl = response.base64.startsWith('data:')
        ? response.base64
        : `data:application/pdf;base64,${response.base64}`;

      setPersonalizedContractUrl(pdfUrl);
      setShowPreview(true);

      trackEvent('lmi_contract_form_preview_generated', {
        state: selectedState,
        contractVersionId: contract.id,
      });
    } catch (error: unknown) {
      trackError({
        error: new Error('Failed to generate contract preview'),
        extras: {
          originalError: error,
          contractVersionId: contract.id,
          utilityAccountId,
        },
        tags: {
          module: 'lmi-contract-form',
          action: 'preview',
        },
      });

      trackEvent('lmi_contract_form_preview_failed', {
        state: selectedState,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      addNotification({
        message: getCopy('previewError'),
        variant: 'error',
        displayTime: NOTIFICATION_DISPLAY_TIME,
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleAcceptContract = async () => {
    if (!user || !contract || !tabsData || !personalizedContractUrl) return;

    setIsSubmittingContract(true);

    trackEvent('lmi_contract_form_accept_clicked', {
      state: selectedState,
      contractVersionId: contract.id,
    });

    try {
      await createContractAgreements([
        {
          userId: user.id,
          utilityAccountId,
          contractVersionId: contract.id,
          userAgent: navigator.userAgent,
        },
      ]);

      trackEvent('lmi_contract_form_agreement_created', {
        state: selectedState,
        contractVersionId: contract.id,
      });

      addNotification({
        message: getCopy('acceptanceSuccess'),
        variant: 'success',
        displayTime: NOTIFICATION_DISPLAY_TIME,
      });

      history.push(`${settings}/check-lmi-eligibility`);
    } catch (error: unknown) {
      trackError({
        error: new Error('Failed to create contract agreement'),
        extras: {
          originalError: error,
          contractVersionId: contract.id,
          utilityAccountId,
        },
        tags: {
          module: 'lmi-contract-form',
          action: 'accept',
        },
      });

      trackEvent('lmi_contract_form_agreement_failed', {
        state: selectedState,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      addNotification({
        message: getCopy('acceptanceError'),
        variant: 'error',
        displayTime: NOTIFICATION_DISPLAY_TIME,
      });
    } finally {
      setIsSubmittingContract(false);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  if (!config || !selectedState) {
    return null;
  }

  return (
    <>
      <BackLink to={`${settings}/check-lmi-eligibility`} />
      <Page maxWidth="800px">
        <FormContainer>
          <Text textStyle="heading800" tag="h1" margin={{ bottom: '16px' }}>
            {config.title}
          </Text>
          <Text margin={{ bottom: '32px' }}>{config.description}</Text>

          {isLoadingData && <Text>{getCopy('loadingContractData')}</Text>}

          {dataError && (
            <Text color="error" margin={{ bottom: '24px' }}>
              {dataError}
            </Text>
          )}

          {!isLoadingData && !dataError && (
            <>
              <EligibilitySection>
                <SectionTitle>Eligibility details</SectionTitle>
                <SectionDescription>
                  Select your county and household occupancy below to calculate
                  your county's household income limit.
                </SectionDescription>

                <Text margin={{ bottom: '16px' }} color="secondary">
                  <strong>Utility Account:</strong> {utilityName}
                </Text>

                <TabsData
                  state={selectedState}
                  onChange={handleTabsDataChange}
                  counties={counties}
                  incomeLimits={incomeLimits}
                  programOptions={programOptions}
                  existingProofDocs={validProofDocs ?? null}
                />

                <Text
                  margin={{ top: '16px' }}
                  color="secondary"
                  textStyle="paragraph400"
                >
                  Your information is used only to pre-fill your verification
                  form and won't be shared outside the state program.
                </Text>
              </EligibilitySection>

              {(() => {
                const qualField = getQualificationField();
                return qualField ? (
                  <>
                    <div style={{ marginTop: '32px' }} />
                    <QualificationFields
                      field={qualField}
                      value={qualificationType}
                      onChange={setQualificationType}
                      interpolations={getInterpolations()}
                      formData={{
                        county: tabsData?.county ?? '',
                        householdOccupancy: tabsData?.houseOccupancy ?? '',
                        qualifyingProgram: tabsData?.lmiProgram ?? '',
                      }}
                    />
                  </>
                ) : null;
              })()}
              <ButtonContainer>
                <Button
                  onClick={handlePreviewContract}
                  disabled={!isFormReady() || isLoadingPreview}
                  loading={isLoadingPreview}
                  type="button"
                  fullWidth
                >
                  {isLoadingPreview
                    ? getCopy('generatingPreview')
                    : 'Review form'}
                </Button>
              </ButtonContainer>
            </>
          )}
        </FormContainer>
      </Page>

      {showPreview && contract && config && (
        <ContractPreviewDialog
          isOpen={showPreview}
          onClose={handleClosePreview}
          onAccept={handleAcceptContract}
          pdfUrl={personalizedContractUrl}
          contractDisplayName={contract.displayName}
          isSubmitting={isSubmittingContract}
          preText={config.contractLink.preText}
          postText={config.contractLink.postText}
        />
      )}
    </>
  );
};
